import React, { FC, forwardRef, useEffect, useRef, useState } from 'react';

interface IInputSelectorProps {
  values: any[];
  value?: any;
  onSelect?: (value: any) => void;
  onChange?: (value: any) => void;
  selectorClassname?: string;
  optionsClassname?: string;
  chevronDownClassname?: string;
  formatValue?: (val: string) => string;
  placeholder?: string;
  showEmptySelect?: boolean;
  name?: string;
  ref?: any;
}

/**
 * @param props - The props for the component.
 * @param placeholder - placeholder for the input.
 * @param selectorClassname - custom class name for the select container.
 * @param optionsClassname - custom class name for the select option.
 * @param chevronDownClassname - custom class name for the select chevron.
 * @param onSelect - Function which is called after selecting value
 * @param onChange - Function which is called after selecting value
 * @param formatValue - Function to format option value
 * @param showEmptySelect - Indicates to show empty select
 * @param ref - ref for the input.
 * @param values - values for select.
 * @param value - value for select.
 * @returns A React component that displays a selector.
 */

const InputSelector: FC<IInputSelectorProps> = forwardRef((props, ref) => {
  const {
    onSelect,
    values,
    value,
    selectorClassname,
    optionsClassname,
    chevronDownClassname,
    placeholder = 'Select',
    formatValue,
    onChange,
    showEmptySelect = true,
    ...rest
  } = props;

  const [opened, setOpened] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isError, setIsError] = useState(false);
  const selectorRef = useRef<any>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const setParentValues = (value: string) => {
    if (onSelect) {
      onSelect(value);
    }
    if (onChange) {
      onChange(value);
    }
  };

  const onOptionClick = (option: any) => {
    setOpened(false);
    setParentValues(option);
    setInputValue(option);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpened(true);
    setParentValues(event.target.value);
    setInputValue(event.target.value);
    if (filterValues(event.target.value).length === 0) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  };

  const filterValues = (value = inputValue) => {
    return inputValue
      ? values.filter(
          (val) => val && val.toLowerCase().includes(value.toLowerCase())
        )
      : values;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      setOpened(false);
    } else if (event.key === 'Enter' && filterValues().length > 0) {
      setParentValues(filterValues()[0]);
      setInputValue(filterValues()[0]);
      setOpened(false);
    }
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
      data-opened={opened}
      ref={selectorRef}
    >
      <input
        autoComplete="off"
        style={{
          border: isError ? '1px solid #f04438' : undefined,
        }}
        type="text"
        value={inputValue ?? ''}
        onChange={handleInputChange}
        onClick={() => {
          setOpened(true);
        }}
        placeholder={placeholder}
        className={selectorClassname}
        ref={ref}
        {...rest}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      />
      {opened && filterValues().length > 0 && (
        <div className={optionsClassname}>
          {filterValues().map((optionValue, index) => (
            <div
              key={`${optionValue}_${index}`}
              onClick={() => onOptionClick(optionValue)}
              data-selected={optionValue == inputValue}
            >
              {optionValue}
              <span />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export { InputSelector };
